<template>
  <div class="trip-group">
    <div class="group-header">
      <div class="group-info-container">
        <span class="group-id-label">Heli: {{ heliNumber }} - Group: {{ groupIndex + 1 }}</span>
        <span class="group-info">
          Clients: {{ clientCount }} | Total Weight: {{ totalWeight }}lb | FP: {{ fuelPercentage }}%
        </span>
      </div>
      <button class="collapse-btn" @click="isCollapsed = !isCollapsed">
        <span v-if="!isCollapsed">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-caret-down" viewBox="0 0 16 16">
            <path d="M3.204 5h9.592L8 10.481zm-.753.659 4.796 5.48a1 1 0 0 0 1.506 0l4.796-5.48c.566-.647.106-1.659-.753-1.659H3.204a1 1 0 0 0-.753 1.659"/>
          </svg>
        </span>
        <span v-else>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-caret-up" viewBox="0 0 16 16">
            <path d="M3.204 11h9.592L8 5.519zm-.753-.659 4.796-5.48a1 1 0 0 1 1.506 0l4.796 5.48c.566-.647.106-1.659-.753-1.659H3.204a1 1 0 0 1-.753-1.659"/>
          </svg>
        </span>
      </button>
      <button
        v-if="!isCollapsed"
        class="delete-group-btn"
        @click="deleteGroup(groupId)"
        :disabled="clientCount > 0"
      >
        Delete Group
        <svg style="margin-left: 15px;" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
          <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5"/>
        </svg>
      </button>
    </div>

    <!-- Copy Group Button - Top Right -->
    <button class="copy-group-btn-top-right" @click="openCopyModal" title="Copy group to another date">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-copy" viewBox="0 0 16 16">
        <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
      </svg>
    </button>
    <div v-if="!isCollapsed" class="group-details">
      <div class="guide-dropdown">
        <label>Guide 1: </label>
        <select v-model="selectedGuideId" @change="updateGuide" style="margin-left: 10px;">
          <option disabled value="">Select a Guide</option>
          <option
            v-for="availableGuide in groupGuides"
            :key="availableGuide.staffid"
            :value="availableGuide.staffid">
            {{ availableGuide.person.firstname }} {{ availableGuide.person.lastname }}{{ availableGuide.isDeleted ? ' (D)' : '' }}
          </option>
        </select>
        <button v-if="selectedGuideId" @click="clearGuide" class="clear-guide-btn" title="Clear guide">×</button>
        <button v-if="selectedGuideId && !showAdditionalGuideDropdown" @click="showAdditionalGuideDropdown = true">+</button>
      </div>
      <div class="guide-dropdown" v-if="showAdditionalGuideDropdown">
        <label>Guide 2: </label>
        <select v-model="selectedGuideAdditionalId" @change="updateGuide" style="margin-left: 10px;">
          <option disabled value="">Select an Additional Guide</option>
          <option
            v-for="availableGuide in groupGuides"
            :key="availableGuide.staffid"
            :value="availableGuide.staffid">
            {{ availableGuide.person.firstname }} {{ availableGuide.person.lastname }}{{ availableGuide.isDeleted ? ' (D)' : '' }}
          </option>
        </select>
        <button v-if="selectedGuideAdditionalId" @click="clearAdditionalGuide" class="clear-guide-btn" title="Clear additional guide">×</button>
      </div>
      <div class="copy-section">
      </div>
      <div class="clients-section">
        <label class="group-members-label">Group Members:</label>
        <div class="client-grid">
          <div v-for="(client, index) in localClients" :key="client.reservationid" class="client-card">
            <div class="client-information-sections">
              <span class="age-emoji" :style="getAgeStyle(client.person.age)">{{ getAgeEmoji(client.person.age) }}</span>
              <span class="client-name">{{ client.person.firstname }} {{ client.person.lastname }}</span>
              <button class="remove-client-btn" @click="emitRemoveClient(client, index)">
                <svg style="margin-bottom:3px;" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5"/>
                </svg>
              </button>
            </div>
            <div class="row beacon-selection-section">
              <label>Transceivers:</label>
              <select 
                v-model="client.selectedBeaconId" 
                @change="onBeaconSelect(index)"
                :class="{ 'guessed-beacon': client.beacon && client.beacon.beaconguessed }"
              >
                <option disabled value="">Select a Transceiver</option>
                <option v-for="beacon in allBeacons" :key="beacon.beaconid" :value="beacon.beaconid">
                  {{ beacon.beaconnumber }}
                </option>
              </select>
              <div 
                v-if="client.beacon && client.beacon.beaconguessed" 
                class="beacon-guess-label"
              >
                Found previous beacon
              </div>
            </div>
            <div class="row weight-input-section">
              <label>Weight:</label>
              <input type="number" v-model.number="client.person.weight" @change="updateClientWeight(client)" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Copy To Date Modal -->
    <div v-if="showCopyModal" class="modal-overlay" @click="showCopyModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Copy Group to Date</h3>
          <button @click="showCopyModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Target Date:</label>
            <DatePicker v-model="copyTargetDate" @change="checkForConflicts" />
          </div>

          <div v-if="isCheckingConflict" class="checking-section">
            <div class="alert alert-info">
              Checking for conflicts on {{ copyTargetDate }}...
            </div>
          </div>

          <div v-if="copyConflict" class="conflict-section">
            <!-- Copy Already Exists Error (old trip-level) -->
            <div v-if="copyConflict.type === 'copy_already_exists'" class="alert alert-danger">
              <strong>Copy Already Exists:</strong> {{ copyConflict.error }}
              <br><small>Please select a different date or edit the existing copy (Trip {{ copyConflict.existingCopyId }}).</small>
            </div>

            <!-- This Group Already Copied -->
            <div v-else-if="copyConflict.type === 'group_already_copied'" class="alert alert-danger">
              <strong>Group Already Copied:</strong> {{ copyConflict.error }}
              <br><small>Please select a different date or edit the existing copy (Group {{ copyConflict.existingGroupCopyId }} on Trip {{ copyConflict.existingTripId }}).</small>
            </div>

            <!-- Some Groups Already Copied (new group-level) -->
            <div v-else-if="copyConflict.type === 'groups_already_copied'" class="alert alert-warning">
              <strong>Some Groups Already Copied:</strong> {{ copyConflict.error }}
              <br><small>You can still copy remaining groups that haven't been copied yet.</small>
            </div>

            <!-- Helicopter/Pilot Conflict -->
            <div v-else class="alert alert-warning">
              <strong>Conflict Detected:</strong> A trip with the same helicopter and pilot already exists on {{ copyTargetDate }}.
              <br><strong>Helicopter:</strong> {{ copyConflict.helicopter?.model }} ({{ copyConflict.helicopter?.callsign }})
              <br><strong>Pilot:</strong> {{ copyConflict.pilot?.firstname }} {{ copyConflict.pilot?.lastname }}
            </div>

            <!-- Conflict Resolution Options (only for helicopter/pilot conflicts) -->
            <div v-if="copyConflict.type !== 'copy_already_exists' && copyConflict.type !== 'group_already_copied' && copyConflict.type !== 'groups_already_copied'" class="conflict-options">
              <label>
                <input type="radio" v-model="conflictResolution" value="add_to_existing" />
                Copy to same heli, new group
              </label>
              <label>
                <input type="radio" v-model="conflictResolution" value="create_new_trip" />
                Copy to a new heli as the same group
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showCopyModal = false" class="btn btn-secondary">Cancel</button>
          <button
            @click="copyGroup"
            class="btn btn-primary"
            :disabled="!copyTargetDate ||
                      (copyConflict && (copyConflict.type === 'copy_already_exists' || copyConflict.type === 'group_already_copied')) ||
                      (copyConflict && copyConflict.type !== 'copy_already_exists' && copyConflict.type !== 'group_already_copied' && copyConflict.type !== 'groups_already_copied' && !conflictResolution)"
          >
            {{ copyConflict && (copyConflict.type === 'copy_already_exists' || copyConflict.type === 'group_already_copied') ? 'Cannot Copy - Already Exists' :
               copyConflict && copyConflict.type === 'groups_already_copied' ? 'Copy Remaining Groups' :
               copyConflict ? 'Resolve & Copy' : 'Copy Group' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EquipmentDataService from '@/services/EquipmentDataService';
import TripDataService from '@/services/TripDataService';
import ClientsDataService from '@/services/ClientsDataService';
import DatePicker from '../Shared/DatePicker.vue';

export default {
  components: { DatePicker },
  props: {
    groupId: Number,
    groupIndex: Number,
    heliNumber: Number,
    guide: {
      type: [Object, null],
      default: () => null,
      required: false
    },
    guideAdditional: {
      type: [Object, null],
      default: () => null,
      required: false
    },
    clients: {
      type: Array,
      default: () => [],
    },
    allGuides: {
      type: Array,
      required: true,
    },
    allBeacons: Array,
    groupEndDate: String,
    selectedDate: {
      type: String,
      required: true,
    },
    tripId: {
      type: Number,
      required: true,
    }
  },
  data() {
    return {
      isCollapsed: true,
      localClients: this.initializeClients(this.clients || []),
      continueTillDate: this.groupEndDate || '',
      localEndDate: this.groupEndDate || '', // Local copy to avoid prop mutation
      fuelPercentage: 0,
      showAdditionalGuideDropdown: !!(this.guideAdditional && this.guideAdditional.guideid),
      selectedGuideId: this.guide ? this.guide.guideid : '',
      selectedGuideAdditionalId: this.guideAdditional ? this.guideAdditional.guideid : '',
      // Copy to date functionality
      showCopyModal: false,
      copyTargetDate: '',
      copyConflict: null,
      conflictResolution: '',
      isCheckingConflict: false,
      // Group-specific guide data (includes soft-deleted if assigned)
      groupGuides: []
    };
  },
  methods: {
    getAgeEmoji(age) {
      if (age < 18) {
        return '🐣'; // Kid emoji
      } else if (age >= 75) {
        return '👴🏻'; // Senior
      } else {
        return '\u00A0'; // No emoji for adults between 18 and 74
      }
    },
    getAgeStyle(age) {
      if (age < 18) {
        return {
          fontSize: '25px',
          backgroundColor: 'lightgreen',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          width: '33px',
          height: '33px'
        };
      } else if (age >= 75) {
        return {
          fontSize: '25px',
          backgroundColor: 'lightcoral',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          width: '33px',
          height: '33px'
        };
      } else {
        return {};
      }
    },
    deleteGroup(groupId) {
      this.$emit('deleteGroup', groupId);
      this.refetchFuelPercentage();
    },
    emitRemoveGuide(guide) {
      this.$emit('removeGuide', guide);
    },
    emitRemoveClient(client, index) {
      this.localClients.splice(index, 1);
      this.$emit('removeClient', { tripClientId: client.tripClientId, groupId: this.groupId });
      this.refetchFuelPercentage();
    },
    emitDateChange() {
      this.$emit('updateEndDate', { groupId: this.groupId, newEndDate: this.localEndDate });
      this.refetchFuelPercentage();
    },
    openCopyModal() {
      // Set initial date to the current selected date
      this.copyTargetDate = this.formatDateForInput(this.selectedDate);
      this.showCopyModal = true;
      this.copyConflict = null;
      this.conflictResolution = '';
    },
    async checkForConflicts() {
      if (!this.copyTargetDate) return;

      this.isCheckingConflict = true;
      this.copyConflict = null;
      this.conflictResolution = '';

      try {
        // Emit event to parent to check for conflicts
        this.$emit('checkCopyConflict', {
          tripId: this.tripId,
          groupId: this.groupId, // Include the specific group ID for conflict checking
          targetDate: this.copyTargetDate,
          callback: (error) => {
            this.isCheckingConflict = false;
            if (error) {
              if (error.response && error.response.status === 409) {
                // Check the type of conflict
                const responseData = error.response.data;
                if (responseData.conflictType === 'copy_already_exists') {
                  // Copy already exists conflict (old trip-level logic)
                  this.copyConflict = {
                    type: 'copy_already_exists',
                    error: responseData.error,
                    existingCopyId: responseData.existingCopyId,
                    targetDate: responseData.targetDate
                  };
                } else if (responseData.conflictType === 'group_already_copied') {
                  // This specific group already copied
                  this.copyConflict = {
                    type: 'group_already_copied',
                    error: responseData.error,
                    existingGroupCopyId: responseData.existingGroupCopyId,
                    existingTripId: responseData.existingTripId,
                    targetDate: responseData.targetDate
                  };
                } else if (responseData.conflictType === 'groups_already_copied') {
                  // Some groups already copied (new group-level logic)
                  this.copyConflict = {
                    type: 'groups_already_copied',
                    error: responseData.error,
                    existingCopies: responseData.existingCopies,
                    targetDate: responseData.targetDate
                  };
                } else {
                  // Helicopter/pilot conflict
                  this.copyConflict = responseData.conflict;
                }
              } else {
                console.error('Error checking for conflicts:', error);
              }
            } else {
              // No conflict
              this.copyConflict = null;
            }
          }
        });

      } catch (error) {
        this.isCheckingConflict = false;
        console.error('Error checking for conflicts:', error);
      }
    },
    async copyGroup() {
      if (!this.copyTargetDate) return;

      // If there's a conflict and no resolution selected, don't proceed
      if (this.copyConflict && !this.conflictResolution) {
        alert('Please select a conflict resolution option.');
        return;
      }

      try {
        this.$emit('copyGroup', {
          groupId: this.groupId,
          targetDate: this.copyTargetDate,
          conflictResolution: this.conflictResolution
        });

        // Reset modal state
        this.showCopyModal = false;
        this.copyTargetDate = '';
        this.copyConflict = null;
        this.conflictResolution = '';

      } catch (error) {
        console.error('Error copying group:', error);
        alert('Error copying group: ' + (error.response?.data?.message || error.message));
      }
    },
    formatDateForInput(dateString) {
      if (!dateString) return '';
      // Convert ISO date string to YYYY-MM-DD format for HTML date input
      return new Date(dateString).toISOString().split('T')[0];
    },
    assignBeacon(beaconId, tripClientId) {
      EquipmentDataService.assignBeaconToTripClient(beaconId, tripClientId)
        .then(() => {
          console.log(`Beacon ${beaconId} assigned to TripClient ${tripClientId} successfully.`);
        })
        .catch(error => {
          console.error("Error assigning beacon to trip client:", error);
        });
    },
    onBeaconSelect(clientIndex) {
      const client = this.localClients[clientIndex];
      if (client.selectedBeaconId && client.tripClientId) {
        this.assignBeacon(client.selectedBeaconId, client.tripClientId);
      }
    },
    initializeClients(clients) {
      return clients.map(client => ({
        ...client,
        selectedBeaconId: client.beacon ? client.beacon.beaconid : null,
      }));
    },
    refetchFuelPercentage() {
      TripDataService.getTripGroupFuelPercentage(this.groupId, this.selectedDate)
        .then(response => {
          this.fuelPercentage = response.data.fuelPercentage;
        })
        .catch(error => {
          console.error("Error fetching fuel percentage:", error);
        });
  },
    updateClientWeight(client) {
      const updateData = {
        weight: client.person.weight,
      };
      ClientsDataService.updateWeight(client.person.id, updateData)
        .then(() => {
          this.refetchFuelPercentage();
        })
        .catch(error => {
          console.error("Error updating client weight:", error);
        });
    },
    updateGuide() {
      this.$emit('update-guides', this.groupId, this.selectedGuideId, this.selectedGuideAdditionalId);
      this.refetchFuelPercentage();
    },
    clearGuide() {
      this.selectedGuideId = '';
      this.selectedGuideAdditionalId = '';
      this.showAdditionalGuideDropdown = false;
      this.updateGuide();
    },
    clearAdditionalGuide() {
      this.selectedGuideAdditionalId = '';
      this.updateGuide();
    },
    // Fetch guides specific to this trip group (includes soft-deleted if assigned)
    async fetchGroupGuides() {
      try {
        const response = await TripDataService.fetchGuidesForTripGroup(this.tripId, this.groupId);
        this.groupGuides = response.data;
      } catch (error) {
        console.error('Error fetching group guides:', error);
        // Fallback to the general guide list if group-specific fetch fails
        this.groupGuides = this.allGuides || [];
      }
    }
  },
  watch: {
    clients: {
      handler(newValue) {
        this.localClients = this.initializeClients(newValue || []);
        this.refetchFuelPercentage();
      },
      deep: true,
      immediate: true,
    },
    guide: {
      handler(newValue) {
        this.selectedGuideId = newValue ? newValue.guideid : '';
      },
      immediate: true
    },
    guideAdditional: {
      handler(newValue) {
        this.selectedGuideAdditionalId = newValue ? newValue.guideid : '';
        this.showAdditionalGuideDropdown = !!newValue;
      },
      immediate: true
    },
    groupEndDate: {
      handler(newValue) {
        this.localEndDate = newValue || '';
      },
      immediate: true
    }
  },
  computed: {
    clientCount() {
      return this.localClients ? this.localClients.length : 0;
    },
    totalWeight() {
      return this.localClients.reduce((total, client) => {
        const weight = parseInt(client.person && client.person.weight);
        return total + (isNaN(weight) ? 0 : weight);
      }, 0);
    }
  },
  created() {
    this.localClients = this.initializeClients(this.clients || []);
    this.refetchFuelPercentage();
    this.fetchGroupGuides();
  },
};
</script>

<style scoped>
.age-emoji {
  justify-self: center; /* Center the age emoji in its column */
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: #f5f5f5;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.group-info-container {
  display: flex;
  flex-direction: column;
}

.group-id-label {
  color: var(--primary-text-eph-color);
  margin-bottom: 10px;
}

.collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  margin-right: 20px;
}

.trip-group {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  position: relative;
  margin-bottom: 15px;
}

.delete-group-btn {
  background-color: #dc3545;
  color: #fff;
  border-radius: 10px;
  border: none;
  position: absolute;
  bottom: 6px;
  right: 25%;
  cursor: pointer;
  width: 200px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.guide-section,
.continue-section {
  margin-bottom: 10px;
}

.guide-dropdown {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-guide-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.2s;
}

.clear-guide-btn:hover {
  background-color: #c82333;
}

.clear-guide-btn:active {
  background-color: #bd2130;
}

.group-members-label {
  margin-bottom: 10px;
}

.group-details {
  border: 1px solid #ccc;
  border-top: none;
  padding: 0 15px 15px;
  margin-bottom: 35px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.group-info {
  background-color: #333;
  color: #fff;
  font-size: 0.875rem;
  padding: 5px 10px;
  border-radius: 15px;
  display: inline-block;
}

.dropdown-text {
  display: block;
  background-color: #fff;
  padding: 5px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.copy-to-btn {
  margin: 5px 0;
  font-size: 0.9em;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

.modal-body {
  padding: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
}

.form-control {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.conflict-section {
  margin-top: 1rem;
}

.alert {
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.alert-warning {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
}

.alert-info {
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

.conflict-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.conflict-options label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clients-section {
  display: flex;
  flex-direction: column;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.clients-section .client-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-left: -10px
}

.client-tag {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #dedede;
  border-radius: 16px;
  padding: 5px 10px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.client-card {
  background-color: #dedede;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: stretch; /* Align items to stretch to take full width */
  gap: 10px;
}

.client-information-section, .beacon-selection-section, .weight-input-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.client-information-sections {
  display: grid;
  grid-template-columns: 40px 1fr 40px; /* Ensures a gap remains for age emoji even if not present */
  align-items: center;
  gap: 10px;
  width: 100%; /* Make the width equal to the client-card */
}

.client-name {
  flex-grow: 1; /* Allow the name to take up remaining space */
}

.beacon-selection-section select, .weight-input-section input {
  flex-grow: 1;
  max-width: 100px; /* Adjust the width of the inputs */
  margin-left: 45px;
  padding-left: 15px;
}

.beacon-selection-section label, .weight-input-section label {
  font-size: 12px; /* Make the label text smaller */
  color: #333; /* Dark grey text */
  margin-bottom: -8px; /* Remove bottom margin */
  margin-left: 32px
}

.client-tag:hover {
  background-color: #ccc;
}

.remove-client-btn {
  justify-self: end; /* Align the delete button to the right end */
  width: 25px;
  background-color: #dc3545;
  border: none;
  border-radius: 50%;
  margin-bottom: 25px;
  color: white;
  cursor: pointer;
  padding: 0 6px;
  line-height: 1.5;
}

.remove-client-btn:hover {
  background-color: #c82333;
}

.guessed-beacon {
  border: 2px solid orange;
  border-radius: 4px;
}

.beacon-guess-label {
  color: orange;
  font-size: 10px;
  margin-top: -10px;
  margin-left: 8px
}

.delete-group-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.copy-group-btn-top-right {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 6px;
  border-radius: 4px;
  border: 1px solid #0d6efd;
  background: white;
  color: #0d6efd;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.copy-group-btn-top-right:hover {
  background-color: #0d6efd;
  color: white;
}

.copy-group-btn-top-right svg {
  pointer-events: none;
}

</style>
